// dataSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import baseUrl from './baseUrl';

// Define an initial state
const initialState = {
    redeemReqRecords: [],
    refundReqRecords: [],
    searching: 'idle',
    totalRedeemReqCount: 0,
    totalRefundReqCount: 0,
    status: 'idle',
    error: null,
    data: [],
    comments: [],
    search: []
};

// Define the asynchronous action for fetching advertiserSupportRecords
export const redeemRequestsDownload = createAsyncThunk(
    'redeemRequestsDownload',
    async (_, thunkAPI) => {
        try {
            const response = await fetch(`${baseUrl}/request/download`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                    // Add any other necessary headers here
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const contentDisposition = response.headers.get(
                'Content-Disposition'
            );
            let fileName = 'Redeem-requests.xlsx';

            if (
                contentDisposition &&
                contentDisposition.includes('attachment')
            ) {
                const fileNameMatch =
                    contentDisposition.match(/filename="(.+)"/);
                if (fileNameMatch && fileNameMatch?.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const refundRequestsDownload = createAsyncThunk(
    'refundRequestsDownload',
    async (_, thunkAPI) => {
        try {
            const response = await fetch(`${baseUrl}/refund/download`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                    // Add any other necessary headers here
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const contentDisposition = response.headers.get(
                'Content-Disposition'
            );
            let fileName = 'Refund-requests.xlsx';

            if (
                contentDisposition &&
                contentDisposition.includes('attachment')
            ) {
                const fileNameMatch =
                    contentDisposition.match(/filename="(.+)"/);
                if (fileNameMatch && fileNameMatch?.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const fetchRedeemReqList = createAsyncThunk(
    'data/fetchRedeemReqList',
    async currentPage => {
        const response = await axios.get(
            `${baseUrl}/request/getAll/${currentPage}`
        );
        return response.data;
    }
);

export const fetchRefundReqList = createAsyncThunk(
    'data/fetchRefundReqList',
    async currentPage => {
        const response = await axios.get(
            `${baseUrl}/refund/list/${currentPage}`
        );
        return response.data;
    }
);

export const fetchRedeemReqCount = createAsyncThunk(
    'data/fetchRedeemReqCount',
    async userId => {
        const response = await axios.get(`${baseUrl}/request/getTotalCount`);
        if (response.data > 0) {
            return response.data;
        }
    }
);
export const fetchRefundReqCount = createAsyncThunk(
    'data/fetchRefundReqCount',
    async userId => {
        const response = await axios.get(`${baseUrl}/refund/getTotalCount`);
        if (response.data > 0) {
            return response.data;
        }
    }
);
export const fetchSupportDetailsBySupportId = createAsyncThunk(
    'data/fetchSupportDetailsBySupportId',
    async supportId => {
        const response = await axios.get(
            `${baseUrl}/support/ticket/${supportId}`
        );
        return response.data;
    }
);

export const fetchCommentsBySupportID = createAsyncThunk(
    'data/fetchCommentsBySupportID',
    async supportId => {
        try {
            const response = await fetch(
                `${baseUrl}/comments/getData/${supportId}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const responseData = await response.json();

            return responseData;
        } catch (error) {
            console.error('Error fetching data:', error);
            throw error;
        }
    }
);
export const searchRedeemRequestList = createAsyncThunk(
    'users/searchRedeemRequestList',
    async ({ userId, searchText }) => {
        const response = await axios.get(
            `${baseUrl}/request/searchAll?searchTerm=${searchText.trim()}`
        );
        return response.data;
    }
);
export const searchRefundRequestList = createAsyncThunk(
    'users/searchRefundRequestList',
    async searchText => {
        const response = await axios.get(
            `${baseUrl}/refund/searchAll?searchTerm=${searchText.trim()}`
        );
        return response.data;
    }
);
// Create a slice
const requestSlice = createSlice({
    name: 'support',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(refundRequestsDownload.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(refundRequestsDownload.fulfilled, state => {
                state.loading = false;
            })
            .addCase(refundRequestsDownload.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(redeemRequestsDownload.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(redeemRequestsDownload.fulfilled, state => {
                state.loading = false;
            })
            .addCase(redeemRequestsDownload.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        builder
            .addCase(fetchRedeemReqList.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchRedeemReqList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.redeemReqRecords = action.payload;
            })
            .addCase(fetchRedeemReqList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchRefundReqList.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchRefundReqList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.refundReqRecords = action.payload;
            })
            .addCase(fetchRefundReqList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });

        builder
            .addCase(fetchRedeemReqCount.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchRedeemReqCount.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalRedeemReqCount = action.payload;
            })
            .addCase(fetchRedeemReqCount.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchRefundReqCount.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchRefundReqCount.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalRefundReqCount = action.payload;
            })
            .addCase(fetchRefundReqCount.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchSupportDetailsBySupportId.pending, state => {
                state.status = 'loading';
            })
            .addCase(
                fetchSupportDetailsBySupportId.fulfilled,
                (state, action) => {
                    state.status = 'succeeded';
                    state.data = action.payload;
                }
            )
            .addCase(
                fetchSupportDetailsBySupportId.rejected,
                (state, action) => {
                    state.status = 'failed';
                    state.error = action.error.message;
                }
            );
        builder
            .addCase(fetchCommentsBySupportID.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchCommentsBySupportID.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.comments = action.payload;
            })
            .addCase(fetchCommentsBySupportID.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(searchRedeemRequestList.pending, state => {
                state.searching = 'loading';
            })
            .addCase(searchRedeemRequestList.fulfilled, (state, action) => {
                state.searching = 'succeeded';
                state.search = action.payload;
            })
            .addCase(searchRedeemRequestList.rejected, (state, action) => {
                state.searching = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(searchRefundRequestList.pending, state => {
                state.searching = 'loading';
            })
            .addCase(searchRefundRequestList.fulfilled, (state, action) => {
                state.searching = 'succeeded';
                state.search = action.payload;
            })
            .addCase(searchRefundRequestList.rejected, (state, action) => {
                state.searching = 'failed';
                state.error = action.error.message;
            });
    }
});

export default requestSlice.reducer;
